<template>
  <div class="min-h-full">
    <main class="py-10">
      <!-- Page header -->
      <div class="max-w-3xl mx-auto px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">
        <div class="flex items-center space-x-5">
          <div class="flex-shrink-0">
            <div class="relative">
              <img
                class="h-10 w-14 object-cover"
                :src="request.engagement.place.image"
                :alt="request.engagement.place.name"
              >
              <span
                class="absolute inset-0 shadow-inner rounded-full"
                aria-hidden="true"
              />
            </div>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-gray-900">
              {{ request.engagement.place.name }}
            </h1>
            <p class="text-sm font-medium text-gray-500">
              {{ request.engagement.place.area }}
            </p>
          </div>
        </div>
        <template v-if="request.offer_made_at === null && request.declined_at === null && !request.expired">
          <div
            v-if="isTheGuide"
            class="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3"
          >
            <button
              type="button"
              class="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
              @click="showDeclineModal = true"
            >
              Decline
            </button>
            <button
              type="button"
              class="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
              @click="showOfferModal = true"
            >
              Make Offer
            </button>
          </div>
          <div
            v-else-if="isTheOperator"
            class="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3"
          >
            <button
              type="button"
              class="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
              @click="showCancelModal = true"
            >
              Cancel request
            </button>
          </div>
        </template>
      </div>

      <div class="mt-8 max-w-3xl mx-auto sm:px-6 lg:max-w-7xl">
        <div class="space-y-6">
          <!-- Description list-->
          <section aria-labelledby="applicant-information-title">
            <div class="bg-white shadow sm:rounded-lg">
              <div class="flex justify-between items-center px-4 py-5 sm:px-6">
                <div>
                  <h2
                    id="applicant-information-title"
                    class="text-lg leading-6 font-medium text-gray-900"
                  >
                    Engagement Information
                  </h2>
                  <p class="mt-1 max-w-2xl text-sm text-gray-500">
                    Information regarding the guide engagement
                  </p>
                </div>
                <div>
                  <span
                    v-if="request.offer ? request.offer.booked_at !== null : false"
                    class="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800"
                  > Booked </span>
                  <span
                    v-else-if="request.offer_made_at !== null"
                    class="inline-flex rounded-full bg-green-100 px-3.5 py-0.5 font-semibold leading-5 text-green-800"
                  > Offer given {{ request.offer.currency.symbol }} {{ request.offer.amount }} </span>
                  <span
                    v-else-if="request.declined_at !== null"
                    class="inline-flex items-center px-3.5 py-0.5 rounded-full font-medium bg-red-100 text-red-800"
                  > Declined </span>
                  <span
                    v-else-if="request.expired"
                    class="inline-flex items-center px-3.5 py-0.5 rounded-full font-medium bg-red-100 text-red-800"
                  > Expired </span>
                  <human-readable-time
                    v-else
                    :date="request.respond_by"
                    prefix="Deadline in "
                    suffix=""
                  />
                </div>
              </div>
              <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
                <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                  <div class="sm:col-span-2">
                    <dt class="text-sm font-medium text-gray-500">
                      Description
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ request.engagement.description }}
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Starts at
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      <FormattedTime :date="request.engagement.starts_at" />
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Ends at
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      <FormattedTime :date="request.engagement.ends_at" />
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Pax
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ request.engagement.pax }}
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Tour Operator
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900 flex items-center">
                      <div class="flex items-center space-x-2">
                        <div class="flex-shrink-0">
                          <div class="relative">
                            <img
                              class="h-6 w-6 rounded-full object-cover"
                              :src="request.engagement.team.photo_url"
                              :alt="request.engagement.team.name"
                            >
                            <span
                              class="absolute inset-0 shadow-inner rounded-full"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <div>
                          <h1 class="text-xl font-bold text-gray-900">
                            {{ request.engagement.team.name }}
                          </h1>
                          <p class="text-sm font-medium text-gray-500">
                            {{ request.engagement.user.name }}
                          </p>
                        </div>
                      </div>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </section>

          <section
            v-if="!isTheGuide"
            aria-labelledby="applicant-information-title"
          >
            <div class="bg-white shadow sm:rounded-lg">
              <div class="flex justify-between items-center px-4 py-5 sm:px-6">
                <div>
                  <h2
                    id="guide-information"
                    class="text-lg leading-6 font-medium text-gray-900"
                  >
                    Guide Information
                  </h2>
                  <p class="mt-1 max-w-2xl text-sm text-gray-500">
                    Information regarding the requested guide
                  </p>
                </div>
              </div>
              <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
                <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Description
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      <div class="flex items-center">
                        <div class="h-10 w-10 flex-shrink-0">
                          <img
                            class="h-10 w-10 rounded-full object-cover"
                            :src="guide.user.profile_photo_url"
                            :alt="guide.user.name"
                          >
                        </div>
                        <div class="ml-4">
                          <div class="font-medium text-gray-900">
                            {{ guide.user.name }}
                          </div>
                          <div class="text-gray-500">
                            {{ guide.team.name }}
                          </div>
                        </div>
                      </div>
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Company
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      <div class="flex items-center space-x-2">
                        <div class="flex-shrink-0">
                          <div class="relative">
                            <img
                              class="h-6 w-6 rounded-full object-cover"
                              :src="guide.team.photo_url"
                              :alt="guide.team.name"
                            >
                            <span
                              class="absolute inset-0 shadow-inner rounded-full"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <div>
                          <h1 class="text-xl font-bold text-gray-900">
                            {{ guide.team.name }}
                          </h1>
                          <p class="text-sm font-medium text-gray-500">
                            {{ guide.user.name }}
                          </p>
                        </div>
                      </div>
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      E-mail
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ guide.email }}
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Experience
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ guide.experience }}
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="text-sm font-medium text-gray-500">
                      Knowledge
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ guide.knowledge }}
                    </dd>
                  </div>
                  <div class="sm:col-span-2">
                    <dt class="text-sm font-medium text-gray-500">
                      Tour Types
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ guide.tour_types }}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </section>

          <h3>Comments</h3>
          <div class="mt-8 flex flex-col">
            <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white px-2 py-6">
                  <ul class="space-y-4">
                    <li
                      v-for="comment in request.comments"
                      class="flex justify-start"
                      :class="{'flex-row-reverse': comment.user.id !== $page.props.user.id}"
                    >
                      <img
                        class="h-10 w-10 rounded-full object-cover mx-4"
                        :src="comment.user.profile_photo_url"
                        :alt="comment.user.name"
                      >
                      <div
                        class="relative max-w-xl px-4 py-2 rounded shadow"
                        :class="[comment.user.id !== $page.props.user.id ? 'bg-green-200' : 'bg-blue-200']"
                      >
                        <span class="block">{{ comment.comment }}</span>
                      </div>
                    </li>
                  </ul>
                  <div
                    v-if="!request.expired && request.declined_at === null"
                    class="bg-gray-50 px-4 py-6"
                  >
                    <div class="flex space-x-3">
                      <div class="flex-shrink-0">
                        <img
                          class="h-10 w-10 rounded-full"
                          :src="$page.props.user.profile_photo_url"
                          :alt="$page.props.user.name"
                        >
                      </div>
                      <div class="min-w-0 flex-1">
                        <div>
                          <label
                            for="comment"
                            class="sr-only"
                          >About</label>
                          <textarea
                            id="comment"
                            v-model="form.comment"
                            name="comment"
                            rows="3"
                            class="shadow-sm block w-full focus:ring-blue-500 focus:border-blue-500 sm:text-sm border border-gray-300 rounded-md"
                            placeholder="Add a note"
                          />
                        </div>
                        <p
                          v-if="form.errors.comment"
                          id="comment-error"
                          class="mt-2 text-sm text-red-600"
                        >
                          {{ form.errors.comment }}
                        </p>
                        <div class="mt-3 flex items-center justify-between">
                          <button
                            type="button"
                            class="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            @click="comment"
                          >
                            Comment
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <EngagementRequestOffer
      :show="showOfferModal"
      :request="request"
      :currencies:="currencies"
      @close="showOfferModal = false"
    />

    <AlertModal
      :show="showDeclineModal"
      @close="showDeclineModal = false"
    >
      <template #default>
        <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon
            class="h-6 w-6 text-red-600"
            aria-hidden="true"
          />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <DialogTitle
            as="h3"
            class="text-lg leading-6 font-medium text-gray-900"
          >
            Decline request
          </DialogTitle>
          <div class="mt-2">
            <p class="text-sm text-gray-500">
              Are you sure you want to decline the request? The request will be permanently declined, and it cannot be undone.
            </p>
          </div>
        </div>
      </template>
      <template #buttons>
        <button
          type="button"
          class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          @click="decline"
        >
          Decline
        </button>
        <button
          ref="cancelButtonRef"
          type="button"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
          @click="showDeclineModal = false"
        >
          Cancel
        </button>
      </template>
    </AlertModal>

    <AlertModal
      :show="showCancelModal"
      @close="showCancelModal = false"
    >
      <template #default>
        <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon
            class="h-6 w-6 text-red-600"
            aria-hidden="true"
          />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <DialogTitle
            as="h3"
            class="text-lg leading-6 font-medium text-gray-900"
          >
            Cancel request
          </DialogTitle>
          <div class="mt-2">
            <p class="text-sm text-gray-500">
              Are you sure you want to cancel the request?
            </p>
          </div>
        </div>
      </template>
      <template #buttons>
        <button
          type="button"
          class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          @click="decline"
        >
          Cancel the request
        </button>
        <button
          ref="cancelButtonRef"
          type="button"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
          @click="showCancelModal = false"
        >
          Nevermind
        </button>
      </template>
    </AlertModal>
  </div>
</template>

<script>
import { DialogTitle } from '@headlessui/vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import HumanReadableTime from '../../ComponentsDeprecated/Time/HumanReadableTime';
import Modal from '../../Jetstream/Modal.vue';
import EngagementRequestOffer from '@/ComponentsDeprecated/Modal/EngagementRequestOffer';
import AlertModal from '@/ComponentsDeprecated/Modal/AlertModal';
import FormattedTime from '@/ComponentsDeprecated/Time/FormattedTime';

export default {
  name: 'Show',
  components: {
    FormattedTime,
    AlertModal,
    HumanReadableTime,
    Modal,
    EngagementRequestOffer,
    DialogTitle,
    ExclamationTriangleIcon,
  },
  props: {
    request: {
      required: true,
      type: Object,
    },

    guide: {
      required: true,
      type: Object,
    },
    isTheGuide: {
      required: true,
      type: Boolean,
    },
    isTheOperator: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return {
      form: this.$inertia.form({
        comment: '',
      }),
      showDeclineModal: false,
      showOfferModal: false,
      showCancelModal: false,
    };
  },
  computed: {},
  watch: {},
  methods: {
    comment() {
      this.form.post(route('engagements.requests.comment.store', { engagementRequest: this.request.id }), {
        errorBag: 'storeComment',
        preserveScroll: true,
        onSuccess: () => this.form.reset('comment'),
      });
    },
    decline() {
      this.showDeclineModal = false;
      this.showCancelModal = false;
      this.$inertia.delete(route('engagements.requests.decline', { engagementRequest: this.request.id }));
    },
  },
};
</script>

<style scoped>

</style>
